import { Component, Inject, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { ToolboxTalkTemplateModel } from 'src/app/models/toolbox-talk-template.model';
import { UtilsService } from '../../shared/utils.service';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { FileService } from 'src/app/shared/file.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToolboxTalksTemplatesEditDialogComponent } from '../toolbox-talks-templates-edit-dialog/toolbox-talks-templates-edit-dialog.component';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { UserModel } from 'src/app/models/user.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { ToolboxTalksTemplatesSnippetsSelectorComponent } from '../toolbox-talks-templates-snippets-selector/toolbox-talks-templates-snippets-selector.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
    selector: 'app-toolbox-talks-templates-edit',
    templateUrl: './toolbox-talks-templates-edit.component.html',
    styleUrls: ['./toolbox-talks-templates-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ToolboxTalksTemplatesEditComponent implements OnInit {

  toolbox_talk_template: ToolboxTalkTemplateModel = new ToolboxTalkTemplateModel();

  newFiles: FileList;

  inProgress: boolean = false;

  assignee: UserModel = {} as UserModel;

  canEdit: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public router: Router,
    public renderer: Renderer2,
    public utils: UtilsService,
    private fileService: FileService,
    public rbac: RBACService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialog: MatDialogRef<ToolboxTalksTemplatesEditComponent | ToolboxTalksTemplatesEditDialogComponent>
  ) {
  }

  ngOnInit() {
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS, RBACService.RBAC_A.A_EDIT)
    ], true);

    if (this.dialogData.id && (typeof this.dialogData.id == 'number')) {
      this.find(this.dialogData.id);
    } else {
      // If the toolbox talk template data is present, the "Create From" button was pressed.
      if( this.dialogData.toolbox_talk ) {
        this.toolbox_talk_template.apply(this.dialogData.toolbox_talk);
      }
    }
  }

  onRemoveFiles() {
    this.utils.showComponentDialog(
      FilesRemoveComponent,
      this.toolbox_talk_template.files,
      {
        width: '700px'
      },
      () => this.find(this.toolbox_talk_template.id)
    );
  }

  async onSubmit(form: NgForm, continueWhenDone: boolean = false) {
    // make sure the form data is valid
    if (form.valid) {

      if ( this.inProgress ) {
        return;
      }

      this.inProgress = true;

      // Only set the account id if this is a new toolbox talk template.
      if ( !this.toolbox_talk_template.id ) {
        this.toolbox_talk_template.account_id = this.dialogData.account_id ? this.dialogData.account_id : 0;
      }

      this.toolbox_talk_template.agenda = this.toolbox_talk_template.agenda.replace(/><li[^>]*>/g, ">\n<li>")

      const toolboxCopy = { ...this.toolbox_talk_template };

      // try to upload the files and handle the response gracefully.
      try {
        toolboxCopy.files = await this.fileService.createManyFromForm(this.newFiles);
      } catch (error) {
        // Reset the in progress status.
        this.inProgress = false;
        return;
      }

      // check if the site should be created or updated
      if (this.toolbox_talk_template.id) {
        this.update(toolboxCopy as ToolboxTalkTemplateModel, continueWhenDone);
      } else {
        this.create(toolboxCopy as ToolboxTalkTemplateModel);
      }
    } else {
      alert('Please make sure you enter all fields with valid information.');
    }
  }

  /**
   * Make a user selection for Toolbox Talk reported by users.
   */
  onSelectReportedBy() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: this.toolbox_talk_template.reported_by ? [this.toolbox_talk_template.reported_by] : []
      },
      {},
      (userId: number) => {
        if (!userId) { return; }
        this.toolbox_talk_template.reported_by = userId;
      }
    );
  }

  onSelectSnippets() {
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesSnippetsSelectorComponent,
      { multiple: true },
      {
        width: '1200px'
      },
      (selectedIds: number[]) => {
        if (Array.isArray(selectedIds) && selectedIds.length > 0) {
          this.api.makeRequest(
            'get',
            `v1/toolbox-talks-templates/${selectedIds.join(',')}`
          )
            .then((response: any) => {
              const templates = response.data as ToolboxTalkTemplateModel[];
              this.appendToAgenda(templates);
            })
            .catch((err) => this.utils.handleAPIErrors(err));
        }
      }
    );
  }

  appendToAgenda(templates: ToolboxTalkTemplateModel[]) {
    const formattedAgendas = templates
      .filter(t => t.agenda?.trim())
      .map(t => `<h3>${t.name}</h3>\n${t.agenda.trim()}`);

    if (formattedAgendas.length === 0) {
      return
    }

    const mergedAgenda = formattedAgendas.join('\n\n');

    if (!this.toolbox_talk_template.agenda) {
      this.toolbox_talk_template.agenda = mergedAgenda;
    } else {
      this.toolbox_talk_template.agenda += `\n\n${mergedAgenda}`;
    }
  }

  private find(toolbox_talks_id: number) {
    this.api.makeRequest(
      'get',
      'v1/toolbox-talks/' + toolbox_talks_id,
      {},
      {
        template: 'true'
      }
    ).then((response: any) => {
      this.toolbox_talk_template = new ToolboxTalkTemplateModel().apply(response.data); // Apply the data instead of overriding the object.
      this.toolbox_talk_template.user_ids = this.toolbox_talk_template.users.map(
        (user) => user.id
      );
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
    });
  }

  private create(toolboxTalk: ToolboxTalkTemplateModel, callback?: any) {
    this.api.makeRequest(
      'post',
      'v1/toolbox-talks',
      toolboxTalk,
      {
        template: 'true'
      }
    ).then((response: any) => {
      this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + ' was created.');

      this.toolbox_talk_template.id = response.data.id;

      this.inProgress = false;

      if (typeof callback === 'function') {
        callback();
      }

      this.dialog.close(true);
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
      this.inProgress = false;
    });
  }

  private update(toolboxTalk: ToolboxTalkTemplateModel, continueWhenDone: boolean = false) {
    this.api.makeRequest(
      'put',
      'v1/toolbox-talks/' + toolboxTalk.id,
      toolboxTalk,
      {
        template: 'true'
      }
    ).then((response: any) => {
      this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + ' was updated.');
      this.inProgress = false;
      this.newFiles = null;
      this.toolbox_talk_template = response;
      if(continueWhenDone) {
        this.dialog.close();
        this.router.navigate([`/toolbox-talks-templates/${toolboxTalk.id}/view/`]);
      } else {
        this.find(toolboxTalk.id);
      }
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
      this.inProgress = false;
    });
  }
}
